import { Card, CardContent, Divider, Grid, Menu, MenuItem, Typography } from "@mui/material";
import { withStyles, WithStyles } from "@mui/styles";
import * as api from "api/Knowledge";
import { FeedStyleClasses } from "assets/jss/common-styles";
import ButtonEllipsis from "components/Buttons/ButtonEllipsis";
import ButtonExpander from "components/Buttons/ButtonExpander";
import ButtonExpandToView from "components/Buttons/ButtonExpandToView";
import IconButtonEdit from "components/Buttons/IconButtonEdit";
import { SpinnerIndicator } from "components/CallServer";
import DrawerTextInput from "components/Dialogs/DrawerTextInput";
import IconSocialComments from "components/Icons/IconSocialComments";
import IconSocialLikes from "components/Icons/IconSocialLikes";
import IconSocialThanks from "components/Icons/IconSocialThanks";
import IconSocialViews from "components/Icons/IconSocialViews";
import MuiRegularButton from "components/MuiExtras/Button/MuiRegularButton";
import { GridEx } from "components/MuiExtras/Grid/GridEx";
import MuiConfirmation from "components/MuiExtras/MuiConfirmation";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiColor from "components/MuiExtras/Typography/MuiColor";
import MuiMuted from "components/MuiExtras/Typography/MuiMuted";
import { withSnackbar, WithSnackbar } from "components/SnackbarProvider/Snackbar";
import UserProfileLink from "components/UserProfileLink";
import { Formaters } from "lib/Formarters";
import HtmlUtils from "lib/HtmlUtils";
import { FeedType, PublicationType } from "models/ApiEnums";
import { FeedItemStatisticsDto, PublicationAdminItemDto } from "models/ApiModels";
import * as React from "react";
import { Trans, WithTranslation, withTranslation } from "react-i18next";
import { RoutePaths } from "routes/RouteConfig";
import PublicationAdminBody from "views/Publication/Components/PublicationAdminBody";
import PublicationCommentsDialog from "views/Publication/Dialogs/PublicationCommentsDialog";
import PublicationLogDialog from "views/Publication/Dialogs/PublicationLogDialog";
import PublicationRelationshipsDialog from "views/Publication/Dialogs/PublicationRelationshipsDialog";
import PublicationViewersDrawer from "views/Publication/Dialogs/PublicationViewersDrawer";
import FlagsLine from "views/Timeline/Components/CardComp/FlagsLine";
import StatusLine from "views/Timeline/Components/CardComp/StatusLine";
import TitleLine from "views/Timeline/Components/CardComp/TitleLine";
import { TopicsLine } from "views/Timeline/Components/CardComp/TopicsLine";
import { getPublicationSecondViewAction } from "views/Timeline/Components/FeedHelpers";
import PublicationHighlightDrawer, { PublicationHighlightDrawerResult } from "views/Timeline/Dialogs/PublicationHighlightDrawer";
import PublicationLikesReviewDrawer from "views/Timeline/Dialogs/PublicationLikesReviewDrawer";
import PublicationThanksReviewDrawer from "views/Timeline/Dialogs/PublicationThanksReviewDrawer";
import TranscriptionSubtitleDialog from "views/Timeline/Dialogs/TranscriptionSubtitleDialog";
import VideoSearchSubtitlesDrawer from "views/Timeline/Dialogs/VideoSearchSubtitlesDrawer";

/////////////////////////////////////////////////////////////////////////////////////

interface IState {
    mouseOver: boolean;
    menuAnchorEl: any;
    dialog: any;
}

export interface IProps {
    item: PublicationAdminItemDto;
    onEdit: (publicationId: string, publicationType: PublicationType) => void;
    onDelete: (publicationId: string, publicationType: PublicationType) => void;
    onExpand: (publicationId: string, publicationType: PublicationType) => void;
    onNeedReload: () => void;
}

type Props = IProps & WithSnackbar & WithTranslation & WithStyles<typeof FeedStyleClasses>;

/////////////////////////////////////////////////////////////////////////////////////

class PublicationAdminCard extends React.Component<Props, IState> {

    constructor(props: Props) {
        super(props);

        this.state = {
            dialog: null,
            mouseOver: false,
            menuAnchorEl: null
        };
    }

    ///
    private handleCloseMenu() {
        this.setState({ menuAnchorEl: null, mouseOver: false });
    }

    ///
    private handleAudit = () => {
        this.setState({
            menuAnchorEl: null,
            mouseOver: false,
            dialog: <PublicationLogDialog
                publicationId={this.props.item.publicationId}
                onClose={() => this.setState({ dialog: null })}
            />
        });
    };

    ///
    private handleView = () => {
        const { item: { publicationId, publicationType } } = this.props;

        this.setState({
            mouseOver: false,
            menuAnchorEl: null,
        }, () => getPublicationSecondViewAction(publicationType, publicationId)());
    };

    ///
    private handleLikeReviewClick = () => {
        this.setState({
            menuAnchorEl: null,
            dialog: <PublicationLikesReviewDrawer
                publicationId={this.props.item.publicationId}
                onClose={() => this.setState({ dialog: null })}
            />
        });
    };

    ///
    private handleThanksReviewClick = () => {
        this.setState({
            menuAnchorEl: null,
            dialog: <PublicationThanksReviewDrawer
                publicationId={this.props.item.publicationId}
                canAddThanks={false}
                onClose={() => this.setState({ dialog: null })}
            />
        });
    };

    ///
    private handleCommentsClick = () => {
        this.setState({
            menuAnchorEl: null,
            dialog: <PublicationCommentsDialog
                publicationId={this.props.item.publicationId}
                onClose={() => this.setState({ dialog: null })}
            />
        });
    };

    ///
    private handleViewersClick = () => {
        this.setState({
            menuAnchorEl: null,
            dialog: <PublicationViewersDrawer
                publicationId={this.props.item.publicationId}
                onClose={() => this.setState({ dialog: null })}
            />
        });
    };

    ///
    private handleEdit = () => {
        const { item: { publicationId, publicationType } } = this.props;

        this.setState({
            mouseOver: false,
            menuAnchorEl: null,
        });

        this.props.onEdit(publicationId, publicationType);
    };

    ///
    private handleDelete = () => {
        const { item: { publicationId, publicationType } } = this.props;

        this.setState({
            mouseOver: false,
            menuAnchorEl: null,
        });

        this.props.onDelete(publicationId, publicationType);
    };

    ///
    private handleSwitchView = () => {
        const { item: { publicationId, publicationType } } = this.props;

        this.setState({
            mouseOver: false,
            menuAnchorEl: null,
        });

        this.props.onExpand(publicationId, publicationType);
    };

    ///
    private handleBlock = () => {
        const { item: { publicationId } } = this.props;

        this.setState({
            menuAnchorEl: null,
            dialog: (
                <DrawerTextInput
                    confirmText={this.props.t("Common.Buttons.ContinueText")}
                    title={this.props.t("Views.Publication.Components.PublicationAdminCard.BlockTitle")}
                    placeHolder={this.props.t("Views.Publication.Components.PublicationAdminCard.BlockPlaceholder")}
                    rows={5}
                    maxLength={1500}
                    inputOptional={false}
                    onCancel={() => this.handleCloseDialog()}
                    onAccept={
                        (text) => {
                            api.publicationSwapBlock(publicationId, text)
                                .then((result) => {
                                    if (result) {
                                        this.props.onNeedReload();
                                        this.props.snackbar.showMessage(this.props.t("Views.Publication.Components.PublicationAdminCard.BlockSuccess"), "success");
                                    }
                                    this.handleCloseDialog();
                                });
                        }
                    }
                />
            )
        });
    };

    ///
    private handleUnblock = () => {
        const { item: { publicationId, publication: { title, } } } = this.props;

        this.setState({
            menuAnchorEl: null,
            dialog: (
                <MuiConfirmation
                    title={this.props.t("Views.Publication.Components.PublicationAdminCard.ConfirmationUnblockTitle")}
                    message={
                        <Trans
                            i18nKey="Views.Publication.Components.PublicationAdminCard.ConfirmationUnblockMessage"
                            values={{ title: title }}
                            components={
                                [
                                    <b>title</b>,
                                    <br />
                                ]
                            }
                        />
                    }
                    onClose={
                        (confirm) => {
                            if (confirm) {
                                api.publicationSwapBlock(publicationId, null)
                                    .then((result) => {
                                        if (result) {
                                            this.props.onNeedReload();
                                            this.props.snackbar.showMessage(this.props.t("Views.Publication.Components.PublicationAdminCard.UnblockSuccess"), "success");
                                        }
                                        this.handleCloseDialog();
                                    });

                            }
                            else
                                this.handleCloseDialog();
                        }
                    }
                />
            )
        });
    };

    ///
    private handleCloseDialog(): void {
        this.setState({ dialog: null });
    };

    ///
    private handleHighlightClose = (r: PublicationHighlightDrawerResult) => {
        const { item: { publicationId } } = this.props;

        this.handleCloseDialog();

        if (r != null) {
            api.addHighlightPublication(publicationId, r.days)
                .then((newHighlightUntil) => {
                    if (newHighlightUntil != null) {
                        this.props.onNeedReload();
                        this.props.snackbar.showMessage(this.props.t("Views.Publication.Components.PublicationAdminCard.HighlightSuccess", { untildate: newHighlightUntil }), "success");
                    }
                });
        }
    };

    ///
    private handleHighlight = () => {
        this.setState({
            menuAnchorEl: null,
            dialog: <PublicationHighlightDrawer onClose={this.handleHighlightClose} />
        });
    };

    ///
    private handleUndoHighlight = () => {
        const { item: { publicationId } } = this.props;

        this.setState({
            menuAnchorEl: null,
            dialog: (
                <MuiConfirmation
                    title={this.props.t("Views.Publication.Components.PublicationAdminCard.ConfirmationUndoHighlightTitle")}
                    message={this.props.t("Views.Publication.Components.PublicationAdminCard.ConfirmationUndoHighlightMessage")}
                    onClose={
                        (confirm) => {
                            if (confirm) {
                                this.handleCloseDialog();

                                api.cancelHighlightPublication(publicationId)
                                    .then((canceled) => {
                                        if (canceled) {
                                            this.props.onNeedReload();
                                            this.props.snackbar.showMessage(this.props.t("Views.Publication.Components.PublicationAdminCard.UndoHighlightSuccess"), "success");
                                        }
                                    });
                            }
                            else
                                this.handleCloseDialog();
                        }
                    }
                />
            )
        });
    };

    ///
    private handleRelationships() {
        this.setState({
            menuAnchorEl: null,
            dialog: <PublicationRelationshipsDialog
                publicationId={this.props.item.publicationId}
                publicationTitle={this.props.item.publication.title}
                onClose={(changed) => this.setState({ dialog: null })}
            />
        });
    }

    /// 
    private handleSearchSubtitle(fileId: string, publicationId: string) {
        this.setState({
            menuAnchorEl: null,
            dialog: <VideoSearchSubtitlesDrawer
                publicationId={this.props.item.publicationId}
                fileId={fileId}
                subtitle={this.props.item.publication.title}
                onClose={() => this.setState({ dialog: null })}
            />
        });
    };

    /// 
    private handleViewSubtitle(fileId: string, publicationId: string) {
        this.setState({
            menuAnchorEl: null,
            dialog: <TranscriptionSubtitleDialog
                publicationId={this.props.item.publicationId}
                fileId={fileId}
                subtitle={this.props.item.publication.title}
                onClose={() => this.setState({ dialog: null })}
            />
        });
    };

    ///
    private handleDownloadAsset(fileId: string, filename: string) {
        this.setState({ menuAnchorEl: null }, () =>
            HtmlUtils.download(`/Download/${fileId}`, filename)
        );
    }

    ///
    private buildOverflowMenu() {

        const {
            t,
            item: {
                permissions: { canDelete, canEdit, canAddHighlight, canCancelHighlight, canEditRelationships },
                publication: { blocked, containsVttMetadata, videoContainsSubtitles, videoFileId, videoFilename },
                publicationId
            }
        } = this.props;

        const actionMenu: Array<any> = [];

        if (canEdit) {
            actionMenu.push(<MenuItem key="edit" onClick={this.handleEdit}>{t("Common.MenuAction.Edit")}</MenuItem>);
        }

        //
        const arr0 = [];

        if (canEditRelationships) {
            arr0.push(<MenuItem key="relationship" onClick={() => this.handleRelationships()}>{t("Common.MenuAction.EditRelationships")}</MenuItem>);
        }

        if (canAddHighlight) {
            arr0.push(<MenuItem key="highlight" onClick={this.handleHighlight}>{t("Common.MenuAction.Highlight")}</MenuItem>);
        }

        if (blocked)
            arr0.push(<MenuItem key="unblock" onClick={this.handleUnblock}>{t("Common.MenuAction.Unblock")}</MenuItem>);
        else
            arr0.push(<MenuItem key="block" onClick={this.handleBlock}>{t("Common.MenuAction.Block")}</MenuItem>);

        if (canCancelHighlight) {
            arr0.push(<MenuItem key="undohighlight" onClick={this.handleUndoHighlight}>{t("Common.MenuAction.UndoHighlight")}</MenuItem>);
        }

        if (arr0.length > 0) {
            if (actionMenu.length > 0) actionMenu.push(<Divider key="divider0" />);
            actionMenu.push(arr0);
        }

        //
        const arr2 = [];

        if (videoContainsSubtitles == true) {
            arr2.push(<MenuItem key="search" onClick={() => this.handleSearchSubtitle(videoFileId, publicationId)}>{t("Common.MenuAction.VideoSearchSubtitles")}</MenuItem>);
            arr2.push(<MenuItem key="export" onClick={() => this.handleViewSubtitle(videoFileId, null)}>{t("Common.MenuAction.VideoSubtitles")}</MenuItem>);
        }
        else if (containsVttMetadata == true) {
            arr2.push(<MenuItem key="search" onClick={() => this.handleSearchSubtitle(null, publicationId)}>{t("Common.MenuAction.VideoSearchSubtitles")}</MenuItem>);
            arr2.push(<MenuItem key="export" onClick={() => this.handleViewSubtitle(null, publicationId)}> {t("Common.MenuAction.VideoSubtitles")}</MenuItem >);
        }

        if (canEdit && videoFileId != null) {
            arr2.push(<MenuItem key="download" onClick={() => this.handleDownloadAsset(videoFileId, videoFilename)}><MuiColor variant="info">{t("Common.MenuAction.DownloadSourceVideo")}</MuiColor></MenuItem>);
        }

        if (arr2.length > 0) {
            if (actionMenu.length > 0) actionMenu.push(<Divider key="dividerVideo" />);
            actionMenu.push(arr2);
        }

        //
        actionMenu.push(<Divider key="divAudit" />);
        actionMenu.push(<MenuItem key="audit" onClick={this.handleAudit}>{t("Common.MenuAction.Audit")}</MenuItem>);

        //
        if (canDelete) {
            if (actionMenu.length > 0) actionMenu.push(<Divider key="dividerDelete" />);
            actionMenu.push(<MenuItem key="delete" onClick={() => this.handleDelete()}><MuiColor variant="error">{t("Common.MenuAction.Delete")}</MuiColor></MenuItem>);
        }

        return actionMenu.length > 0
            ? (
                <>
                    <ButtonEllipsis
                        onClick={(e) => { this.setState({ menuAnchorEl: e.currentTarget }); }}
                    />
                    <Menu
                        open={this.state.menuAnchorEl != null}
                        anchorEl={this.state.menuAnchorEl}
                        onClose={() => { this.handleCloseMenu(); }}
                    >
                        {actionMenu}
                    </Menu>
                </>
            )
            : null;
    }

    ///
    private renderPublisher() {
        const { item: { publicationType, publication } } = this.props;

        const label = (publicationType == PublicationType.Question)
            ? this.props.t("Common.Feed.Literals.AskedText").toLowerCase()
            : this.props.t("Common.Feed.Literals.PublishedText").toLowerCase();

        const relativeTime = Formaters.relativeTime(publication.creationDate);

        return (
            <>
                <Typography variant="body2" >
                    <MuiMuted>
                        <b><UserProfileLink user={publication.publisher} /></b> {label} {relativeTime}
                    </MuiMuted>
                </Typography>

                <FlagsLine
                    flags={
                        {
                            inHighlightPeriod: publication.inHighlightPeriod,
                            inPriorityView: false
                        }
                    }
                />
            </>
        );
    }

    ///
    private renderTitle() {
        const {
            classes,
            item: {
                publicationType,
                publication: { answers, title, blocked }
            },
        } = this.props;



        return (
            <TitleLine
                classes={classes}
                data={
                    {
                        publicationType,
                        publication: {
                            title: title,
                            blocked: blocked
                        },
                        permissions: {
                            hasAccess: true
                        },
                        statistics: { answers: answers } as FeedItemStatisticsDto
                    }
                }

                fullView={false}
                feedType={FeedType.Any}
                onTitleClick={this.handleView}
                onAnswerDetailsClick={this.handleView}
            />
        );
    }

    ///
    public render() {
        const {
            classes,
            item: {
                publicationType,
                statistics,
                bodyLoading,
                body,
                permissions: { canEdit },
                publication
            }
        } = this.props;

        const { mouseOver } = this.state;


        return (
            <>
                {this.state.dialog}

                <Card className={classes.card}
                    onMouseEnter={() => this.setState({ mouseOver: true })}
                    onMouseLeave={() => this.setState({ mouseOver: false })
                    }
                >
                    <CardContent className={classes.cardContent}>

                        <MuiContainer margin="dense">
                            <Grid container direction="row" justifyContent="space-between" >
                                <GridEx item alignSelf="center">
                                    <TopicsLine
                                        topics={publication.topics}
                                        onTopicClick={(id) => HtmlUtils.openUrlInNewWindow(RoutePaths.TopicViewer.replace(":id", id))}
                                    />
                                </GridEx>
                                <Grid item className={classes.cardActions}>
                                    <IconButtonEdit visible={mouseOver && canEdit} onClick={this.handleEdit} size="small" />
                                </Grid>
                            </Grid>
                        </MuiContainer>

                        {this.renderTitle()}

                        <MuiContainer margin="dense">
                            {this.renderPublisher()}
                        </MuiContainer>

                        <StatusLine
                            feedType={FeedType.PublicationAdmin}
                            data={
                                {
                                    publicationType,
                                    publication
                                }
                            }
                        />

                        {
                            body &&
                            <MuiContainer margin="dense">
                                <PublicationAdminBody publicationType={publicationType} body={body} bestAnswer={null} />
                            </MuiContainer>
                        }

                        <Grid container justifyContent="space-between" alignItems="center">
                            <Grid item>
                                {
                                    bodyLoading
                                        ? <SpinnerIndicator />
                                        : publicationType == PublicationType.Question
                                            ? <ButtonExpandToView
                                                className={classes.expandButton}
                                                onClick={this.handleView}
                                            />
                                            : <ButtonExpander
                                                className={classes.expandButton}
                                                expanded={body != null}
                                                moreText={this.props.t("Views.Publication.Components.PublicationAdminCard.ViewMoreText")}
                                                lessText={this.props.t("Views.Publication.Components.PublicationAdminCard.ViewLessText")}
                                                onClick={this.handleSwitchView}
                                            />
                                }
                            </Grid>

                            <Grid item className={classes.cardActions}>
                                <Grid container>
                                    <Grid item>
                                        <MuiRegularButton
                                            simple
                                            color="transparent"
                                            title={this.props.t("Common.Feed.Actions.ButtonViewsTitle")}
                                            startIcon={<IconSocialViews />}
                                            onClick={this.handleViewersClick}
                                        >
                                            <span>{statistics.views}</span>
                                        </MuiRegularButton>

                                        <MuiRegularButton
                                            simple
                                            color="transparent"
                                            title={this.props.t("Common.Feed.Actions.ButtonLikedTitle")}
                                            startIcon={<IconSocialLikes />}
                                            onClick={this.handleLikeReviewClick}
                                        >
                                            <span>{statistics.likes - statistics.dislikes}</span>
                                        </MuiRegularButton>

                                        <MuiRegularButton
                                            simple
                                            color="transparent"
                                            title={this.props.t("Common.Feed.Actions.ButtonCommentTitle")}
                                            startIcon={<IconSocialComments />}
                                            onClick={this.handleCommentsClick}
                                        >
                                            <span>{statistics.comments}</span>
                                        </MuiRegularButton>

                                        <MuiRegularButton
                                            simple
                                            color="transparent"
                                            title={this.props.t("Common.Feed.Actions.ButtonThanksTitle")}
                                            startIcon={<IconSocialThanks />}
                                            onClick={this.handleThanksReviewClick}
                                        >
                                            <span>{statistics.thanks}</span>
                                        </MuiRegularButton>
                                    </Grid>
                                </Grid>
                            </Grid>

                            <Grid item className={classes.cardActions}>
                                {this.buildOverflowMenu()}
                            </Grid>
                        </Grid>
                    </CardContent>

                </Card>
            </>
        );
    }
}

export default withTranslation()(withStyles(FeedStyleClasses)(withSnackbar(PublicationAdminCard)));